/**
 * Default phase transition checklists for construction projects
 * These checklists help ensure critical items are completed before moving to the next phase
 */

export const PHASE_TRANSITION_CHECKLISTS = {
  // Phase 1 -> 2: Planning & Permitting -> Site Preparation & Foundation
  1: {
    title: "Before Site Preparation",
    subtitle: "Verify these items before beginning excavation and foundation work",
    items: [
      { id: "1-1", text: "Building permits approved and posted on site", required: true },
      { id: "1-2", text: "Erosion control measures installed (silt fence, etc.)", required: true },
      { id: "1-3", text: "Survey stakes and property corners marked", required: true },
      { id: "1-4", text: "Utility locates completed (call 811)", required: true },
      { id: "1-5", text: "Construction plans and blueprints delivered to site", required: false },
      { id: "1-6", text: "Temporary construction entrance installed", required: false },
      { id: "1-7", text: "Portable toilet delivered to site", required: false },
    ]
  },
  
  // Phase 2 -> 3: Site Preparation & Foundation -> Framing
  2: {
    title: "Before Framing",
    subtitle: "Verify foundation work is complete and materials are ready",
    items: [
      { id: "2-1", text: "Foundation inspection passed", required: true },
      { id: "2-2", text: "Foundation waterproofing/damp proofing applied", required: true },
      { id: "2-3", text: "Backfill completed around foundation", required: true },
      { id: "2-4", text: "Lumber and framing materials delivered to site", required: true },
      { id: "2-5", text: "Anchor bolts and foundation hardware verified", required: true },
      { id: "2-6", text: "Sewer and water lines stubbed through foundation", required: false },
      { id: "2-7", text: "Foundation as-built survey completed", required: false },
    ]
  },
  
  // Phase 3 -> 4: Framing -> Exterior & Systems (Rough-in)
  3: {
    title: "Before Rough-In",
    subtitle: "Verify framing is complete and ready for MEP systems",
    items: [
      { id: "3-1", text: "Framing inspection passed", required: true },
      { id: "3-2", text: "Roof sheathing and felt paper installed", required: true },
      { id: "3-3", text: "Windows and exterior doors ordered/on site", required: true },
      { id: "3-4", text: "Plumbing rough-in materials on site", required: true },
      { id: "3-5", text: "Electrical rough-in materials on site", required: true },
      { id: "3-6", text: "HVAC equipment and ductwork ordered", required: true },
      { id: "3-7", text: "Structural hardware and connectors verified", required: false },
      { id: "3-8", text: "Fireplace chase framed (if applicable)", required: false },
    ]
  },
  
  // Phase 4 -> 5: Exterior & Systems -> Insulation & Drywall
  4: {
    title: "Before Insulation & Drywall",
    subtitle: "Critical items that must be complete before walls are closed",
    items: [
      { id: "4-1", text: "Fire blocking installed in all required locations", required: true },
      { id: "4-2", text: "Water meter installed by utility company", required: true },
      { id: "4-3", text: "Gas meter installed and connected", required: true },
      { id: "4-4", text: "Rough plumbing inspection passed", required: true },
      { id: "4-5", text: "Rough electrical inspection passed", required: true },
      { id: "4-6", text: "Rough HVAC inspection passed", required: true },
      { id: "4-7", text: "Temporary electrical outlets ready for drywall crew", required: true },
      { id: "4-8", text: "Temporary lighting installed in work areas", required: true },
      { id: "4-9", text: "Low voltage wiring complete (cable, ethernet, security)", required: false },
      { id: "4-10", text: "Tub/shower units set in place", required: false },
      { id: "4-11", text: "All exterior penetrations sealed", required: false },
    ]
  },
  
  // Phase 5 -> 6: Insulation & Drywall -> Interior Finishes
  5: {
    title: "Before Interior Finishes",
    subtitle: "Verify walls are ready for trim and finishes",
    items: [
      { id: "5-1", text: "Insulation inspection passed", required: true },
      { id: "5-2", text: "Drywall hanging complete", required: true },
      { id: "5-3", text: "Drywall taping and finishing complete", required: true },
      { id: "5-4", text: "Primer coat applied to walls", required: true },
      { id: "5-5", text: "Trim and millwork materials delivered", required: true },
      { id: "5-6", text: "Cabinets ordered and delivery scheduled", required: true },
      { id: "5-7", text: "Flooring materials on site or ordered", required: false },
      { id: "5-8", text: "Interior door hardware selected", required: false },
    ]
  },
  
  // Phase 6 -> 7: Interior Finishes -> Fixtures & Exterior Finishing
  6: {
    title: "Before Fixtures Installation",
    subtitle: "Verify finishes are complete and fixtures are ready",
    items: [
      { id: "6-1", text: "Interior paint complete (walls and ceilings)", required: true },
      { id: "6-2", text: "Trim and baseboards installed", required: true },
      { id: "6-3", text: "Cabinets installed and secured", required: true },
      { id: "6-4", text: "Countertops templated and ordered", required: true },
      { id: "6-5", text: "Flooring installation complete", required: true },
      { id: "6-6", text: "Light fixtures and plumbing fixtures on site", required: true },
      { id: "6-7", text: "Appliances ordered and delivery scheduled", required: false },
      { id: "6-8", text: "Interior doors hung and hardware installed", required: false },
    ]
  },
  
  // Phase 7 -> 8: Fixtures & Exterior Finishing -> Final Walkthrough & Handover
  7: {
    title: "Before Final Walkthrough",
    subtitle: "Final items before project completion",
    items: [
      { id: "7-1", text: "All fixtures installed and operational", required: true },
      { id: "7-2", text: "Appliances installed and tested", required: true },
      { id: "7-3", text: "Final electrical inspection passed", required: true },
      { id: "7-4", text: "Final plumbing inspection passed", required: true },
      { id: "7-5", text: "Final mechanical/HVAC inspection passed", required: true },
      { id: "7-6", text: "Landscaping complete", required: false },
      { id: "7-7", text: "Driveway and sidewalks complete", required: false },
      { id: "7-8", text: "Final grading complete", required: false },
      { id: "7-9", text: "Touch-up painting complete", required: false },
      { id: "7-10", text: "Construction cleaning complete", required: false },
    ]
  },
  
  // Phase 8: Final phase - Certificate of Occupancy
  8: {
    title: "Certificate of Occupancy",
    subtitle: "Final items for project closeout",
    items: [
      { id: "8-1", text: "Final building inspection passed", required: true },
      { id: "8-2", text: "Certificate of Occupancy obtained", required: true },
      { id: "8-3", text: "All lien waivers collected from contractors", required: true },
      { id: "8-4", text: "Warranty documentation compiled", required: false },
      { id: "8-5", text: "Owner walkthrough completed", required: false },
      { id: "8-6", text: "Keys and garage door openers ready", required: false },
      { id: "8-7", text: "Homeowner manuals and documentation prepared", required: false },
    ]
  }
};

/**
 * Get checklist for a specific phase transition
 * @param {number} phaseNumber - The phase number (1-8)
 * @returns {object} Checklist data with title, subtitle, and items
 */
export const getPhaseChecklist = (phaseNumber) => {
  return PHASE_TRANSITION_CHECKLISTS[phaseNumber] || null;
};

/**
 * Get the next phase name for transition messaging
 * @param {number} currentPhase - Current phase number
 * @returns {string} Name of the next phase
 */
export const getNextPhaseName = (currentPhase) => {
  const phaseNames = {
    1: "Site Preparation & Foundation",
    2: "Framing",
    3: "Exterior & Systems (Rough-in)",
    4: "Insulation & Drywall",
    5: "Interior Finishes",
    6: "Fixtures & Exterior Finishing",
    7: "Final Walkthrough & Handover",
    8: "Project Complete"
  };
  return phaseNames[currentPhase + 1] || "Next Phase";
};
